import React, { useState, useEffect } from 'react';
import { collection, query, where, orderBy, getDocs, addDoc, Timestamp } from 'firebase/firestore';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth, db } from '../../firebase';
import { WorkoutSet } from '../../types';
import './WorkoutProgressionView.css';

const WorkoutProgressionView: React.FC = () => {
  const [user] = useAuthState(auth);
  const [lastWorkouts, setLastWorkouts] = useState<{ [key: string]: number }>({});
  const [selectedWorkoutType, setSelectedWorkoutType] = useState<string | null>(null);
  const [selectedWeight, setSelectedWeight] = useState<number | null>(null);
  const [currentSet, setCurrentSet] = useState<WorkoutSet | null>(null);
  const [completedSets, setCompletedSets] = useState<WorkoutSet[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showingCongratulations, setShowingCongratulations] = useState(false);

  const workoutTypes = ["Bench Press", "Squat", "Deadlift", "Clean"];
  const availableWeights = Array.from({ length: 48 }, (_, i) => 95 + i * 10); // 95 to 565

  useEffect(() => {
    if (user) {
      loadLastWorkouts();
    }
  }, [user]);

  useEffect(() => {
    if (selectedWeight && selectedWorkoutType && !currentSet && completedSets.length === 0) {
      console.log('Auto-starting workout with weight:', selectedWeight);
      // Create sets directly in useEffect to avoid stale closure
      const sets: WorkoutSet[] = [
        { id: '1', percentage: 50, setNumber: 1, reps: { min: 10, max: 12 }, isCompleted: false, setWeight: roundDownToNearestFive(selectedWeight * 0.5) },
        { id: '2', percentage: 60, setNumber: 2, reps: { min: 8, max: 10 }, isCompleted: false, setWeight: roundDownToNearestFive(selectedWeight * 0.6) },
        { id: '3', percentage: 70, setNumber: 3, reps: { min: 6, max: 8 }, isCompleted: false, setWeight: roundDownToNearestFive(selectedWeight * 0.7) },
        { id: '4', percentage: 80, setNumber: 4, reps: { min: 4, max: 6 }, isCompleted: false, setWeight: roundDownToNearestFive(selectedWeight * 0.8) },
        { id: '5', percentage: 90, setNumber: 5, reps: { min: 2, max: 4 }, isCompleted: false, setWeight: roundDownToNearestFive(selectedWeight * 0.9) },
      ];

      console.log('Starting workout with sets:', sets);
      console.log('Setting current set to:', sets[0]);
      setCurrentSet(sets[0]);
      setCompletedSets([]);
    }
  }, [selectedWeight, selectedWorkoutType, currentSet, completedSets.length]);

  const loadLastWorkouts = async () => {
    if (!user?.uid) return;

    try {
      const workoutsRef = collection(db, 'workouts');
      const q = query(
        workoutsRef,
        where('userId', '==', user.uid),
        orderBy('dateCompleted', 'desc')
      );
      
      const snapshot = await getDocs(q);
      const workouts: { [key: string]: number } = {};

      snapshot.docs.forEach((doc) => {
        const data = doc.data();
        const type = data.type as string;
        const weight = data.weight as number;

        if (type && weight && !workouts[type]) {
          workouts[type] = weight;
        }
      });

      setLastWorkouts(workouts);
      setIsLoading(false);
    } catch (error: any) {
      console.error('Error loading last workouts:', error);
      setErrorMessage(`Failed to load workouts: ${error.message}`);
      setIsLoading(false);
    }
  };

  const roundDownToNearestFive = (weight: number): number => {
    return Math.floor(weight / 5) * 5;
  };

  const markSetComplete = (actualReps: number) => {
    if (!currentSet) return;

    const completedSet = { ...currentSet, isCompleted: true, actualReps };
    const newCompletedSets = [...completedSets, completedSet];
    setCompletedSets(newCompletedSets);

    // Move to next set or finish workout
    const nextSetNumber = currentSet.setNumber + 1;
    if (nextSetNumber <= 5) {
      const percentages = [50, 60, 70, 80, 90];
      const repRanges = [
        { min: 10, max: 12 },
        { min: 8, max: 10 },
        { min: 6, max: 8 },
        { min: 4, max: 6 },
        { min: 2, max: 4 }
      ];
      
      const nextSet: WorkoutSet = {
        id: nextSetNumber.toString(),
        percentage: percentages[nextSetNumber - 1],
        setNumber: nextSetNumber,
        reps: repRanges[nextSetNumber - 1],
        isCompleted: false,
        setWeight: roundDownToNearestFive(selectedWeight! * percentages[nextSetNumber - 1] / 100)
      };
      setCurrentSet(nextSet);
    } else {
      setCurrentSet(null);
      setShowingCongratulations(true);
    }
  };

  const saveWorkout = async () => {
    if (!user?.uid || !selectedWorkoutType || !selectedWeight) return;

    // Verify all 5 sets are completed
    if (completedSets.length !== 5) {
      setErrorMessage('Workout not completed - not all sets are completed');
      return;
    }

    try {
      await addDoc(collection(db, 'workouts'), {
        userId: user.uid,
        type: selectedWorkoutType,
        weight: selectedWeight,
        sets: completedSets,
        dateCompleted: Timestamp.now(),
        dateCreated: Timestamp.now()
      });

      // Reset state
      setSelectedWorkoutType(null);
      setSelectedWeight(null);
      setCurrentSet(null);
      setCompletedSets([]);
      setShowingCongratulations(false);
      
      // Reload workouts
      await loadLastWorkouts();
    } catch (error: any) {
      console.error('Error saving workout:', error);
      setErrorMessage(`Failed to save workout: ${error.message}`);
    }
  };

  const resetWorkout = () => {
    setSelectedWorkoutType(null);
    setSelectedWeight(null);
    setCurrentSet(null);
    setCompletedSets([]);
    setShowingCongratulations(false);
  };

  // Debug logging
  console.log('Render state:', {
    isLoading,
    showingCongratulations,
    selectedWorkoutType,
    selectedWeight,
    currentSet: currentSet ? `Set ${currentSet.setNumber}` : null,
    completedSetsCount: completedSets.length,
    errorMessage
  });

  if (isLoading) {
    return (
      <div className="workout-progression-view">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading workout data...</p>
        </div>
      </div>
    );
  }

  if (showingCongratulations) {
    return (
      <div className="workout-progression-view">
        <div className="congratulations-screen">
          <div className="congratulations-content">
            <h1>🎉 Congratulations!</h1>
            <h2>Workout Complete</h2>
            <div className="workout-summary">
              <p><strong>{selectedWorkoutType}</strong></p>
              <p><strong>{selectedWeight} lbs</strong></p>
            </div>
            <div className="congratulations-actions">
              <button className="save-button" onClick={saveWorkout}>
                Save Workout
              </button>
              <button className="dismiss-button" onClick={resetWorkout}>
                Dismiss
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!selectedWorkoutType) {
    return (
      <div className="workout-progression-view">
        <h1>Workout Progression</h1>
        <div className="workout-type-selection">
          <h2>Select Workout Type</h2>
          <div className="workout-type-grid">
            {workoutTypes.map((type) => (
              <button
                key={type}
                className="workout-type-button"
                onClick={() => setSelectedWorkoutType(type)}
              >
                <span className="workout-type-name">{type}</span>
                {lastWorkouts[type] && (
                  <span className="last-weight">Last: {lastWorkouts[type]} lbs</span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!selectedWeight) {
    return (
      <div className="workout-progression-view">
        <h1>Select Weight</h1>
        <div className="weight-selection">
          <div className="weight-selection-header">
            <button className="back-button" onClick={() => setSelectedWorkoutType(null)}>
              ← Back
            </button>
            <h2>{selectedWorkoutType}</h2>
          </div>
          <div className="weight-grid">
            {availableWeights.map((weight) => (
              <button
                key={weight}
                className="weight-button"
                onClick={() => {
                  console.log('Weight selected:', weight);
                  setSelectedWeight(weight);
                }}
              >
                {weight}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (currentSet) {
    return (
      <div className="workout-progression-view">
        <div className="workout-set-view">
          <div className="set-header">
            <button className="cancel-button" onClick={resetWorkout}>
              Cancel
            </button>
            <h1>{selectedWorkoutType}</h1>
            <div className="set-progress">
              Set {currentSet.setNumber} of 5
            </div>
          </div>

          <div className="set-details">
            <div className="weight-display">
              <span className="weight-value">{Math.round(currentSet.setWeight)}</span>
              <span className="weight-unit">lbs</span>
            </div>
            <div className="set-info">
              <p>{currentSet.percentage}% of {selectedWeight} lbs</p>
              <p>Target: {currentSet.reps.min}-{currentSet.reps.max} reps</p>
            </div>
          </div>

          <div className="completed-sets">
            <h3>Completed Sets:</h3>
            {completedSets.map((set) => (
              <div key={set.id} className="completed-set">
                <span>Set {set.setNumber}: {Math.round(set.setWeight)} lbs × {set.actualReps} reps</span>
              </div>
            ))}
          </div>

          <div className="reps-input">
            <h3>How many reps did you complete?</h3>
            <div className="reps-buttons">
              {Array.from({ length: 12 }, (_, i) => i + 1).map((reps) => (
                <button
                  key={reps}
                  className="reps-button"
                  onClick={() => markSetComplete(reps)}
                >
                  {reps}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Loading state for when weight is selected but workout hasn't started yet
  if (selectedWorkoutType && selectedWeight && !currentSet) {
    return (
      <div className="workout-progression-view">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Preparing workout...</p>
        </div>
      </div>
    );
  }

  // Error message display
  if (errorMessage) {
    return (
      <div className="workout-progression-view">
        <h1>Workout Progression</h1>
        <div className="error-message">
          <div className="error-content">
            <span className="error-icon">⚠️</span>
            <p>{errorMessage}</p>
            <button 
              className="dismiss-error-button" 
              onClick={() => setErrorMessage(null)}
            >
              Dismiss
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default WorkoutProgressionView;
